import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Card, CardContent, CardHeader, CardTitle } from "../../_newprofiles/components/ui/Card";
import { Button } from "../../_newprofiles/components/ui/Button";
import { Badge } from "../../_newprofiles/components/ui/Badge";
import { Briefcase, Star, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import homeStyles from "./_HomeUserDashboard.module.css";
import featuredStyles from "./_FeaturedBusinesses.module.css";

const ITEMS_TO_SHOW_AT_ONCE = 1; 
const SCROLL_INTERVAL = 5000;

const FeaturedBusinesses = ({ onViewProfile }) => {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchFeaturedBusinesses = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Business/getFeaturedBusinesses`);
        const formattedBusinesses = response.data.map(business => ({
          id: business.businessId,
          name: business.businessName,
          image: business.profileImage || "/img/ws-logo-01-trans-small-1@2x.png",
          description: business.description,
          location: `${business.suburb || 'N/A'}, ${business.postCode || 'N/A'}`,
          rating: business.overallRating !== null ? parseFloat(business.overallRating) : 0,
        }));
        setBusinesses(formattedBusinesses);
      } catch (err) {
        console.error("Error fetching featured businesses:", err);
        setError(err.message || "Failed to fetch featured businesses.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeaturedBusinesses();
  }, []);

  const scrollNext = () => {
    const maxIndex = businesses.length - ITEMS_TO_SHOW_AT_ONCE;
    if (maxIndex < 0) return;
    setCurrentIndex(prevIndex => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
  };

  const scrollPrev = () => {
    const maxIndex = businesses.length - ITEMS_TO_SHOW_AT_ONCE;
    if (maxIndex < 0) return;
    setCurrentIndex(prevIndex => (prevIndex <= 0 ? maxIndex : prevIndex - 1));
  };

  const startAutoScroll = () => {
    if (businesses.length > ITEMS_TO_SHOW_AT_ONCE) {
      intervalRef.current = setInterval(scrollNext, SCROLL_INTERVAL);
    }
  };

  const stopAutoScroll = () => {
    clearInterval(intervalRef.current);
  };

  useEffect(() => {
    startAutoScroll();
    return () => stopAutoScroll();
  }, [businesses.length]);

  useEffect(() => {
    if (containerRef.current?.firstChild && businesses.length > 0) {
      const tile = containerRef.current.children[0];
      const tileWidth = tile.offsetWidth; 
      const gap = 16;
      const scrollAmount = currentIndex * (tileWidth + gap);
      containerRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [currentIndex, businesses.length]);

  if (isLoading) { /* loading JSX */ }
  if (error) { /* error JSX */ }
  if (businesses.length === 0) { /* empty JSX */ }

  const canScroll = businesses.length > ITEMS_TO_SHOW_AT_ONCE;

  return (
    <Card className={homeStyles.cardBase}>
      <CardHeader>
        <CardTitle className={homeStyles.cardTitleContainer}>
          <Briefcase className={`${homeStyles.cardTitleIcon} ${homeStyles.cardTitleIconBlue}`} />
          <span>Featured Businesses</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div 
          className={featuredStyles.carouselWrapper}
          onMouseEnter={stopAutoScroll}
          onMouseLeave={startAutoScroll}
        >
          <div className={featuredStyles.businessTilesContainer} ref={containerRef}>
            {businesses.map((business) => (
              <div key={business.id} className={featuredStyles.businessTile}>
                <img src={business.image} alt={business.name} className={featuredStyles.businessImage} onError={(e) => e.target.src = "/img/ws-logo-01-trans-small-1@2x.png"} />
                <div className={featuredStyles.businessInfo}>
                  <h4 className={featuredStyles.businessName}>{business.name}</h4>
                  <div className={featuredStyles.locationContainer}>
                    <MapPin size={14} className={featuredStyles.locationIcon} />
                    <span className={featuredStyles.businessLocation}>{business.location}</span>
                  </div>
                  
                  {/* --- FIX: ADDED CONDITIONAL RENDERING FOR RATING --- */}
                  <div className={featuredStyles.ratingContainer}>
                    {business.rating > 0 && (
                      <>
                        <Badge className={featuredStyles.ratingBadge}>{business.rating.toFixed(1)}</Badge>
                        <div className={featuredStyles.stars}>
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={16} className={`${featuredStyles.starIcon} ${i < business.rating ? featuredStyles.starFilled : featuredStyles.starEmpty}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <Button variant="outline" size="sm" className={featuredStyles.viewProfileButton} onClick={() => onViewProfile(business.id)}>
                    View Profile
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {canScroll && (
            <div className={featuredStyles.carouselNav}>
              <button onClick={scrollPrev} className={featuredStyles.navButton} aria-label="Previous business">
                <ChevronLeft size={16} />
              </button>
              <button onClick={scrollNext} className={featuredStyles.navButton} aria-label="Next business">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeaturedBusinesses;